import React, { FC, memo, useCallback, useEffect, useState } from 'react'
import cn from 'classnames'
import { useStore } from 'store'
import * as yup from 'yup'
import { SectionContainer } from 'containers/SectionContainer'
import { InnerContainer } from 'containers/InnerContainer'
import { TitleSection } from 'components/TitleSection'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { InputField } from 'components/InputField'
import { TextareaField } from 'components/TextareaField'
import { HeadsetVariant } from './HeadsetVariant'
import { PhoneInput } from 'components/PhoneInput'
import { CustomSelect } from 'components/CustomSelect'
import { ButtonMUI } from 'components/ButtonMUI'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { CodeModal } from './CodeModal'
import { ConfirmReserveModal } from './ConfirmReserveModal'
import { PrivacyPolicyModal } from 'components/PrivacyPolicyModal'


import { getHeadsetVariantInfo, HeadsetVariantType } from '../utils'
import { rawCountries, SiteVariant } from 'utils'
import { IReserve } from 'models'

import { Play } from 'components/Play'
import { toast } from 'react-toastify'

import styles from './styles.module.scss'


interface IProps {
}

export interface IReserveForm {
  name: string;
  phoneNumber: string;
  email: string;
  note?: string
  quantity: string;
  shippingCountry: string;
  variant: string;
}

const countries: any = rawCountries.map(item => ({ value: item[0], label: item[0] }))
const quantity = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' }
]

const schema = yup.object().shape({
  name: yup.string().required('Name should be required please'),
  phoneNumber: yup.string().required('Phone number should be required please'),
  email: yup.string().email().required('Email should be required please'),
  note: yup.string().max(500, 'Max length 500 symbols').optional(),
  quantity: yup.string().required('Quantity should be required please'),
  shippingCountry: yup.string().required('Shipping Country should be required please'),
  variant: yup.string().required('Variant should be required please')
})

const checkEmail = (value: string | undefined | null) => value?.match(
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/g
)

const ReserveSection: FC<IProps> = props => {
  const { reserveVRStore, loadingStore } = useStore()
  const [isConfirmed, setConfirmed] = useState<boolean>(false)
  const [isAccept, setIsAccept] = useState(false)
  const [isOpenCodeModal, setOpenCodeModal] = useState(false)
  const [confirmTimer, setConfirmTimer] = useState<number>(0)
  const [isOpenConfirmReserveModal, setOpenConfirmReserveModal] = useState(false)
  const [phoneInputKey, setPhoneInputKey] = useState(0)

  const { control, handleSubmit, formState: { errors }, reset, watch } = useForm<IReserveForm>({
    resolver: yupResolver(schema)
  })

  const timeOutCallback = useCallback(() => setConfirmTimer(currTimer => currTimer > 0 ? currTimer - 1 : 0), [])

  const watchEmail = watch('email')
  const watchPhoneNumber = watch('phoneNumber')


  useEffect(() => {
    confirmTimer > 0 && setTimeout(timeOutCallback, 1000)
  }, [confirmTimer, timeOutCallback])

  const resetConfirmTimer = () => {
    if (confirmTimer <= 0) {
      setConfirmTimer(60)
    }
  }

  const resetReserveData = () => {
    setConfirmed(false)
    setConfirmTimer(0)
    setPhoneInputKey(prev => prev + 1)
    reset()
  }

  const onSubmit = async (data: IReserveForm) => {
    if (isConfirmed) {
      try {
        if (!isConfirmed) {
          return toast.error('You need to confirm your email')
        }

        loadingStore.setLoading(true)
        const reserveData: IReserve = {
          Name: data.name,
          Country: data.shippingCountry,
          Email: data.email,
          Phone: '+' + data.phoneNumber,
          Quantity: Number(data.quantity),
          Variant: Number(data.variant),
          Text: data.note ?? ''
        }

        await reserveVRStore.reservVRSpot(reserveData)
        toast.success('You have successfully made a reservation')
        setOpenConfirmReserveModal(true)
        resetReserveData()
      } catch (error: any) {
        toast.error(error.message ?? 'Something went wrong. Please try again')
        console.log('error', error)
      } finally {
        loadingStore.setLoading(false)
      }
    } else {
      try {
        loadingStore.setLoading(true)
        await reserveVRStore.confirmEmailVRSpot(data.email, '+' + data.phoneNumber)
        toast.success('Verification code has been sent')
        resetConfirmTimer()
        setOpenCodeModal(true)
      } catch (e: any) {
        toast.error(e.message ?? 'Error while sending verification code')
        console.log('SendCode', e)
      } finally {
        loadingStore.setLoading(false)
      }
    }
  }


  const onConfirmed = () => {
    setConfirmed(true)
    setConfirmTimer(0)
  }


  const handleChangeAccept = () => {
    setIsAccept(prev => !prev)
  }

  const onCloseCodeModal = useCallback(() => {
    setOpenCodeModal(false)
  }, [])

  const onCloseConfirmReserveModal = useCallback(() => {
    setOpenConfirmReserveModal(false)
  }, [])

  return (
    <SectionContainer withBackgroundImage id="reserveYourSpot">
      <InnerContainer>
        <div className={styles.reserveContainer}>
          <TitleSection title={'Reserve your spot, secure your VR1 headset:'}
                        subTitle={'Secure your spot for the early future sale and enjoy a minimum of 24 - 48 hours head start in reserving the VR1 headset, getting ahead of those who haven\'t registered. By reserving your spot, you\'ll ensure a place in the first production batch. Additionally, the quickest people to reserve will receive exclusive gifts and perks.'}/>

          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <div className={cn({
              [styles.inputsBlock]: true,
              [styles.inputsBlockConfirmed]: isConfirmed
            })}>
              <div className={styles.leftBlock}>
                <div className={cn(styles.inputsBox)}>
                  <InputField name={'name'} placeholder={'Name*'} control={control} errors={errors}
                              disabled={isConfirmed}/>
                  <Controller
                    name={'phoneNumber'}
                    control={control}
                    defaultValue=""
                    render={({ field }) => {
                      const { onChange } = field
                      const handleChangePhoneNumber = (selectedOption: string) => {
                        onChange(String(selectedOption))
                      }

                      const error = Boolean(errors && errors['phoneNumber'])

                      return (
                        <PhoneInput inputKey={phoneInputKey}
                                    onChange={handleChangePhoneNumber}
                                    error={error}
                                    disabled={isConfirmed}/>
                      )
                    }}
                  />
                </div>
                <div className={styles.inputsBox}>
                  <CustomSelect name={'shippingCountry'}
                                isSearchable
                                placeholder={'Shipping Country*'}
                                control={control}
                                options={countries}
                                errors={errors}
                                disabled={isConfirmed}/>
                  <CustomSelect name={'quantity'}
                                placeholder={'Quantity*'}
                                control={control}
                                options={quantity}
                                errors={errors}/>

                </div>
                <div className={cn(styles.inputsBox, styles.inputsBoxPhone)} style={{ marginBottom: 0 }}>
                  <InputField name={'email'}
                              placeholder={'Email Address*'}
                              control={control}
                              errors={errors}
                              iconType={'confirm'}
                              disabled={isConfirmed}
                              confirmed={isConfirmed}/>
                  <ButtonMUI styleType="blue"
                             type={'submit'}
                             sx={{ '&&': { fontSize: '1rem' } }}
                             disabled={confirmTimer > 0 || isConfirmed}
                  >
                    {confirmTimer > 0
                      ? `Confirm in ${confirmTimer}`
                      : isConfirmed ? 'Confirmed' : 'Confirm My Email'}
                  </ButtonMUI>
                </div>
                <div className={styles.additionalInfo}>
                  To confirm your authenticity, we kindly ask you to verify your email using the code that you will
                  receive once you click "Confirm My Email" button.
                </div>
              </div>
              <div className={styles.rightBlock}>
                <TextareaField name={'note'}
                               control={control}
                               errors={errors}
                               placeholder={'Leave a note'}
                               disabled={isConfirmed}/>
                <div className={styles.additionalInfo}>
                  Please leave a comment in case you have a special request or comment for your order.
                </div>
              </div>
            </div>
            <div className={cn({
              [styles.variantsBlock]: true,
              [styles.variantsBlockConfirmed]: isConfirmed
            })}>
              <div className={styles.selectPreferred}>
                <Play/>
                <span>Select Your Preferred<br/>Somnium VR1 Variant*</span>
              </div>
              <Controller
                name={'variant'}
                control={control}
                defaultValue=""
                render={({ field }) => {
                  const { onChange, value } = field
                  const selectedVariantNumber = value ? Number(value) : null
                  const handleChange = (selectedOption: number) => {
                    onChange(String(selectedOption))
                  }

                  const error = Boolean(errors && errors['variant'])

                  return (
                    <div className={styles.variants}>
                      <div className={styles.variantsInner}>
                        <HeadsetVariant variant={getHeadsetVariantInfo(HeadsetVariantType.VR_Headset)}
                                        onSelect={handleChange}
                                        selectedVariant={selectedVariantNumber}
                                        error={error}/>
                        <HeadsetVariant variant={getHeadsetVariantInfo(HeadsetVariantType.Eye_Tracking)}
                                        onSelect={handleChange}
                                        selectedVariant={selectedVariantNumber}
                                        error={error}/>
                      </div>
                      <div className={styles.variantsInner}>
                        <HeadsetVariant variant={getHeadsetVariantInfo(HeadsetVariantType.Hand_Tracking)}
                                        onSelect={handleChange}
                                        selectedVariant={selectedVariantNumber}
                                        error={error}/>
                        <HeadsetVariant variant={getHeadsetVariantInfo(HeadsetVariantType.MR_Cameras)}
                                        onSelect={handleChange}
                                        selectedVariant={selectedVariantNumber}
                                        error={error}/>
                      </div>
                    </div>
                  )
                }}
              />
            </div>
            <div className={styles.confirmReserveBlock}>
              <ButtonMUI styleType="blue"
                         disabled={!isAccept || !isConfirmed}
                         sx={{ boxShadow: '0px 0px 15px 0px rgba(0, 204, 255, 0.50)', maxWidth: '445px' }}
                         type={'submit'}>
                Reserve your spot
              </ButtonMUI>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleChangeAccept}
                    sx={{
                      color: '#1474B8'
                    }}
                  />
                }
                label={<div className={styles.acceptLabel}>
                  I accept Somnium Space <PrivacyPolicyModal privacyType={SiteVariant.VR}>
                  {onOpen => <a onClick={event => {
                    event.stopPropagation()
                    event.preventDefault()
                    onOpen()
                  }}>Privacy Policy</a>}</PrivacyPolicyModal>
                </div>}
                sx={{
                  color: '#ffffff',
                  marginRight: 0
                }}
              />
            </div>
          </form>
        </div>
        {isOpenCodeModal
          ? <CodeModal open={isOpenCodeModal}
                       onClose={onCloseCodeModal}
                       phoneNumber={watchPhoneNumber}
                       email={watchEmail}
                       onConfirmed={onConfirmed}
                       timer={confirmTimer}
                       resetTimer={resetConfirmTimer}/>
          : null}
        {isOpenConfirmReserveModal
          ? <ConfirmReserveModal open={isOpenConfirmReserveModal} onClose={onCloseConfirmReserveModal}/>
          : null}
      </InnerContainer>
    </SectionContainer>
  )
}

export default memo(ReserveSection)
